import { TFunction } from "i18next";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { SetStateAction } from "react";
import { HelpCircle } from "lucide-react";
import {
  Dialog,
  FoundationColorTokens,
  Typography as LeoTypography,
  Spacing,
} from "@surya-digital/leo-reactjs-material-ui";
import {
  NavigateToFunctions,
  processProjectParams,
} from "../../../../routes/RoutesHelper";
import { ErrorSuccessDialogContent } from "../ErrorSuccessDialogContent";
import { Stack, Typography } from "@mui/material";
import { ICON_SIZE, getIconProps } from "@pulse/shared-components";
import { ProjectDetailsStore } from "../../store/ProjectDetailsStore";
import {
  DownloadSPoCsCSVDialogState,
  GetPresignedDownloadURLForSPoCssWithFiltersErrors,
} from "../../store/SPoCDownloadAndUnmapStore";

interface DownloadSPoCsCSVDialogProps {
  isDownloadSPoCsCSVDialogVisible: boolean;
  setIsDownloadSPoCsCSVDialogVisible: React.Dispatch<SetStateAction<boolean>>;
  t: TFunction;
  projectStore: Instance<typeof ProjectDetailsStore>;
  navigateTo: NavigateToFunctions;
  tokens: FoundationColorTokens<string>;
  typography: LeoTypography;
  spacing: Spacing;
}

export const DownloadSPoCsCSVDialog = observer(
  ({
    isDownloadSPoCsCSVDialogVisible,
    setIsDownloadSPoCsCSVDialogVisible,
    t,
    projectStore,
    navigateTo,
    tokens,
    typography,
    spacing,
  }: DownloadSPoCsCSVDialogProps): React.ReactElement => {
    const downloadCSVText = t("common.downloadCSV");

    const getErrorText = (): string => {
      switch (projectStore.userAccessStore.spocDownloadStore.rpcErrors) {
        case GetPresignedDownloadURLForSPoCssWithFiltersErrors.DeletedColumnFilter:
          return t("common.deletedColumnFilterErrorText");
        default:
          return t("projects.downloadSPoCsCSV.downloadErrorText");
      }
    };

    const DownloadSPoCsCSVDialogContent = observer((): React.ReactElement => {
      switch (
        projectStore.userAccessStore.spocDownloadStore
          .downloadSPoCsCSVDialogState
      ) {
        case DownloadSPoCsCSVDialogState.DownloadConfirmation:
          return (
            <Stack gap={spacing.spaceXS} direction="row">
              <HelpCircle {...getIconProps(tokens.icon, ICON_SIZE.default)} />
              <Typography
                sx={{ ...typography.b2 }}
                color={tokens.label}
                width={"492px"}
              >
                {t("projects.downloadSPoCsCSV.dialogDescriptionText")}
              </Typography>
            </Stack>
          );
        case DownloadSPoCsCSVDialogState.SuccessState:
          return (
            <ErrorSuccessDialogContent
              spacing={spacing}
              tokens={tokens}
              typography={typography}
              errorSuccessText={t(
                "projects.downloadSPoCsCSV.downloadSuccessText",
              )}
              isError={false}
            />
          );

        case DownloadSPoCsCSVDialogState.ErrorState:
          return (
            <ErrorSuccessDialogContent
              spacing={spacing}
              tokens={tokens}
              typography={typography}
              isError={true}
              errorSuccessText={getErrorText()}
            />
          );
      }
    });

    const getSecondaryButtonText = (): string => {
      switch (
        projectStore.userAccessStore.spocDownloadStore
          .downloadSPoCsCSVDialogState
      ) {
        case DownloadSPoCsCSVDialogState.DownloadConfirmation:
          return t("common.cancel");
        case DownloadSPoCsCSVDialogState.SuccessState:
          return t("common.done");
        case DownloadSPoCsCSVDialogState.ErrorState:
          return t("common.close");
      }
    };

    return (
      <Dialog
        open={isDownloadSPoCsCSVDialogVisible}
        title={downloadCSVText}
        secondaryButtonText={getSecondaryButtonText()}
        onSecondaryButtonClick={() => {
          setIsDownloadSPoCsCSVDialogVisible(false);
          projectStore.userAccessStore.spocDownloadStore.resetSPoCsCSVDialog();
        }}
        primaryButtonText={
          projectStore.userAccessStore.spocDownloadStore
            .isDownloadSPoCsCSVPrimaryButtonVisible
            ? downloadCSVText
            : undefined
        }
        primaryButtonColor="primary"
        onPrimaryButtonClick={async () => {
          await processProjectParams((projectId) => {
            return projectStore.userAccessStore.getPresignedDownloadURLForSPoCs(
              projectId,
              projectStore.projectName,
            );
          }, navigateTo);
        }}
        isSecondaryButtonDisabled={
          projectStore.userAccessStore.spocDownloadStore.isRPCLoading
        }
        isPrimaryButtonDisabled={
          projectStore.userAccessStore.spocDownloadStore.isRPCLoading
        }
        disableBackdropClick
      >
        <DownloadSPoCsCSVDialogContent />
      </Dialog>
    );
  },
);
